import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import LandingLayout from '../../LandingPagesRedesign/LandingLayoutRedesign';
import { tradingApi } from '../../../services/tradingPlatformService';
import './DebtTradingPlatformPage.css';

const SECTIONS = [
  { group: 'Market', items: [
    { id: 'venues', label: '1 · Venue health' },
    { id: 'instruments', label: '2 · Instruments' },
    { id: 'inventory', label: '3 · Inventory' },
  ]},
  { group: 'Trading', items: [
    { id: 'rfq', label: '4–5 · RFQ & quote hit' },
    { id: 'orders', label: '6–7 · Order ticket' },
    { id: 'partner', label: '8 · Partner orders' },
  ]},
  { group: 'Operations', items: [
    { id: 'fixlog', label: '9 · FIX wire log' },
    { id: 'posttrade', label: '10 · Post-trade' },
    { id: 'audit', label: '11 · Recon & audit' },
  ]},
];

const VENUE_OPTIONS = ['AUTO', 'RBI_NDSOM', 'NSE', 'BSE', 'BROKER_A', 'BROKER_B'];
const FIX_VENUES = ['RBI_NDSOM', 'NSE', 'BSE', 'BROKER_A', 'BROKER_B'];

const fmt = (v, dp = 2) => (v == null ? '—' :
  Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }));

const iso = (d) => d.toISOString().slice(0, 10);
const todayIso = () => iso(new Date());
const t1Iso = () => iso(new Date(Date.now() + 86400000));

function Banner({ type, children }) {
  if (!children) return null;
  return <Alert className="dtp-banner" type={type} message={children} showIcon />;
}

function DebtTradingPlatformPage() {
  const [userId, setUserId] = useState('1001');
  const [activeSection, setActiveSection] = useState('venues');
  const [loading, setLoading] = useState(false);

  const [venues, setVenues] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [instrumentRows, setInstrumentRows] = useState([]);
  const [instSettle, setInstSettle] = useState(t1Iso());

  const [invTrade, setInvTrade] = useState(todayIso());
  const [invSettle, setInvSettle] = useState(t1Iso());
  const [invBanner, setInvBanner] = useState(null);
  const [invRows, setInvRows] = useState([]);

  const [rfqIsin, setRfqIsin] = useState('');
  const [rfqSide, setRfqSide] = useState('BUY');
  const [rfqQty, setRfqQty] = useState('1000000');
  const [rfqSettle, setRfqSettle] = useState(t1Iso());
  const [rfqBanner, setRfqBanner] = useState(null);
  const [rfqQuotes, setRfqQuotes] = useState([]);
  const [rfqHitResult, setRfqHitResult] = useState(null);

  const [ordIsin, setOrdIsin] = useState('');
  const [ordSide, setOrdSide] = useState('BUY');
  const [ordType, setOrdType] = useState('LIMIT');
  const [ordQty, setOrdQty] = useState('500000');
  const [ordPrice, setOrdPrice] = useState('99.45');
  const [ordSettle, setOrdSettle] = useState(t1Iso());
  const [ordVenue, setOrdVenue] = useState('AUTO');
  const [ordBanner, setOrdBanner] = useState(null);
  const [blotter, setBlotter] = useState([]);

  const [pKey, setPKey] = useState('demo-key-ext-broker-x');
  const [pIsin, setPIsin] = useState('');
  const [pSide, setPSide] = useState('BUY');
  const [pQty, setPQty] = useState('2000000');
  const [pSettle, setPSettle] = useState(t1Iso());
  const [pBanner, setPBanner] = useState(null);
  const [partnerOrders, setPartnerOrders] = useState([]);

  const [fixVenue, setFixVenue] = useState('NSE');
  const [fixLimit, setFixLimit] = useState('12');
  const [fixMessages, setFixMessages] = useState([]);

  const [ptSettle, setPtSettle] = useState(t1Iso());
  const [ptBanner, setPtBanner] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [contractNotes, setContractNotes] = useState([]);

  const [reconBanner, setReconBanner] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const isinOptions = useMemo(
    () => instruments.map((i) => ({ value: i.isin, label: `${i.isin} — ${i.issuer}` })),
    [instruments]
  );

  const addToBlotter = useCallback((order) => {
    setBlotter((prev) => [order, ...prev]);
  }, []);

  const loadVenues = useCallback(async () => {
    try {
      const data = await tradingApi(userId, '/v1/venues/health');
      setVenues(data);
    } catch (err) {
      message.error(err.message);
    }
  }, [userId]);

  const loadInstruments = useCallback(async () => {
    setLoading(true);
    try {
      const list = await tradingApi(userId, '/v1/instruments');
      setInstruments(list);
      const rows = await Promise.all(
        list.map((i) => tradingApi(userId, `/v1/instruments/${i.isin}`, {
          params: { settlementDate: instSettle },
        }))
      );
      setInstrumentRows(rows);
      if (list.length) {
        const first = list[0].isin;
        setRfqIsin((v) => v || first);
        setOrdIsin((v) => v || first);
        setPIsin((v) => v || first);
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, instSettle]);

  const loadInventory = useCallback(async () => {
    try {
      const r = await tradingApi(userId, '/v1/inventory', {
        params: { tradeDate: invTrade, settlementDate: invSettle },
      });
      const rows = [];
      r.instruments.forEach((inst) => {
        inst.venues.forEach((v, i) => {
          rows.push({ isin: i === 0 ? inst.isin : '', ...v });
        });
      });
      setInvRows(rows);
    } catch (err) {
      message.error(err.message);
    }
  }, [userId, invTrade, invSettle]);

  const refreshInventory = useCallback(async () => {
    try {
      const r = await tradingApi(userId, '/v1/inventory/refresh', {
        method: 'POST',
        params: { tradeDate: invTrade, settlementDate: invSettle },
      });
      setInvBanner({ type: 'success', text: `Ingested ${r.ingested_items} offerings from venues/brokers` });
      loadInventory();
    } catch (err) {
      setInvBanner({ type: 'error', text: err.message });
    }
  }, [userId, invTrade, invSettle, loadInventory]);

  const sendRfq = useCallback(async (e) => {
    e.preventDefault();
    setRfqBanner({ type: 'info', text: 'Fanning out quote request to exchanges + brokers…' });
    try {
      const r = await tradingApi(userId, '/v1/rfq', {
        method: 'POST',
        body: {
          isin: rfqIsin,
          side: rfqSide,
          qty_face_value: rfqQty,
          settlement_date: rfqSettle,
        },
      });
      setRfqQuotes(r.quotes || []);
      setRfqBanner({
        type: 'success',
        text: `${r.quotes.length} quotes received — best: ${r.best_quote.source} @ ${r.best_quote.price_clean}`,
      });
    } catch (err) {
      setRfqBanner({ type: 'error', text: err.message });
    }
  }, [userId, rfqIsin, rfqSide, rfqQty, rfqSettle]);

  const hitQuote = useCallback(async (quoteId, price) => {
    try {
      const o = await tradingApi(userId, '/v1/orders', {
        method: 'POST',
        body: {
          isin: rfqIsin,
          side: rfqSide,
          order_type: 'QUOTE_HIT',
          qty_face_value: rfqQty,
          price_clean: price,
          settlement_date: rfqSettle,
          quote_id: quoteId,
          idempotency_key: `ui-${crypto.randomUUID()}`,
        },
      });
      setRfqHitResult({
        type: 'success',
        text: `Order ${o.cl_ord_id} ${o.status} on ${o.routed_to} — filled ${fmt(o.filled_qty, 0)} @ ${fmt(o.avg_fill_price, 4)} (pinned to quote source)`,
      });
      addToBlotter(o);
    } catch (err) {
      setRfqHitResult({ type: 'error', text: err.message });
    }
  }, [userId, rfqIsin, rfqSide, rfqQty, rfqSettle, addToBlotter]);

  const placeOrder = useCallback(async (e) => {
    e.preventDefault();
    try {
      const body = {
        isin: ordIsin,
        side: ordSide,
        order_type: ordType,
        qty_face_value: ordQty,
        settlement_date: ordSettle,
        venue_preference: ordVenue,
        idempotency_key: `ui-${crypto.randomUUID()}`,
      };
      if (ordType === 'LIMIT') body.price_clean = ordPrice;
      const o = await tradingApi(userId, '/v1/orders', { method: 'POST', body });
      setOrdBanner({
        type: o.status === 'REJECTED' ? 'error' : 'success',
        text: o.status === 'REJECTED'
          ? `Order REJECTED by risk: ${o.reject_reason}`
          : `Order ${o.cl_ord_id} ${o.status} on ${o.routed_to}${o.avg_fill_price ? ` @ ${fmt(o.avg_fill_price, 4)}` : ''}`,
      });
      addToBlotter(o);
    } catch (err) {
      setOrdBanner({ type: 'error', text: err.message });
    }
  }, [userId, ordIsin, ordSide, ordType, ordQty, ordPrice, ordSettle, ordVenue, addToBlotter]);

  const submitPartnerOrder = useCallback(async (e) => {
    e.preventDefault();
    try {
      const r = await tradingApi(userId, '/partner/v1/orders', {
        method: 'POST',
        headers: { 'X-Api-Key': pKey },
        body: {
          isin: pIsin,
          side: pSide,
          order_type: 'MARKET',
          qty_face_value: pQty,
          settlement_date: pSettle,
          idempotency_key: `ui-p-${crypto.randomUUID()}`,
        },
      });
      setPBanner({
        type: 'success',
        text: `Partner ${r.partner_id}: order ${r.order.cl_ord_id} ${r.order.status} on ${r.order.routed_to}`,
      });
      setPartnerOrders((prev) => [r, ...prev]);
    } catch (err) {
      setPBanner({ type: 'error', text: err.message });
    }
  }, [userId, pKey, pIsin, pSide, pQty, pSettle]);

  const loadFixLog = useCallback(async () => {
    try {
      const r = await tradingApi(userId, `/v1/venues/${fixVenue}/fix-log`, {
        params: { limit: fixLimit },
      });
      setFixMessages(r.messages || []);
    } catch (err) {
      message.error(err.message);
    }
  }, [userId, fixVenue, fixLimit]);

  const loadObligations = useCallback(async () => {
    try {
      const obs = await tradingApi(userId, '/v1/posttrade/obligations', {
        params: { settlementDate: ptSettle },
      });
      setObligations(obs);
      const pos = await tradingApi(userId, '/v1/posttrade/positions');
      setPositions(pos);
      const notes = await tradingApi(userId, '/v1/posttrade/contract-notes');
      setContractNotes(notes);
    } catch (err) {
      message.error(err.message);
    }
  }, [userId, ptSettle]);

  const runSettlement = useCallback(async () => {
    try {
      const r = await tradingApi(userId, '/v1/posttrade/settle', {
        method: 'POST',
        params: { settlementDate: ptSettle },
      });
      setPtBanner({
        type: 'success',
        text: `DvP settlement run for ${r.settlement_date}: settled=${r.settled} failed=${r.failed}`,
      });
      loadObligations();
    } catch (err) {
      setPtBanner({ type: 'error', text: err.message });
    }
  }, [userId, ptSettle, loadObligations]);

  const loadAudit = useCallback(async () => {
    try {
      const rec = await tradingApi(userId, '/v1/posttrade/reconciliation');
      setReconBanner({
        type: rec.status === 'CLEAN' ? 'success' : 'error',
        text: `Reconciliation ${rec.status}: contract notes=${rec.contract_notes}, obligations=${rec.obligations}, breaks=${rec.breaks}`,
      });
      const log = await tradingApi(userId, '/v1/audit/routing');
      setAuditRows(log);
    } catch (err) {
      message.error(err.message);
    }
  }, [userId]);

  useEffect(() => {
    loadVenues();
    loadInstruments();
  }, [loadVenues, loadInstruments]);

  const renderIsinSelect = (value, onChange) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {isinOptions.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );

  return (
    <LandingLayout>
      <div className="dtp">
        <div className="dtp-header">
          <div>
            <h1>India Debt Trading Platform</h1>
            <span className="dtp-env">SIMULATED · prototype</span>
          </div>
          <div className="dtp-user">
            <label>
              User ID
              <Input
                size="small"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                style={{ width: 88, marginLeft: 8 }}
              />
            </label>
            <span className="dtp-hint">Demo users: 1001 (cash + G-Sec), 1002 (cash only)</span>
          </div>
        </div>

        <div className="dtp-layout">
          <nav className="dtp-nav">
            {SECTIONS.map((group) => (
              <React.Fragment key={group.group}>
                <div className="dtp-nav-group">{group.group}</div>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={activeSection === item.id ? 'active' : ''}
                    onClick={() => setActiveSection(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </nav>

          <main className="dtp-main">
            {activeSection === 'venues' && (
              <section>
                <h2><span className="dtp-flow">Flow 1</span> Venue &amp; broker connectivity</h2>
                <p className="dtp-sub">RBI NDS-OM, NSE, BSE exchanges + config-driven primary brokers. Dummy connections, SIMULATED mode.</p>
                <div className="dtp-cards">
                  {venues.map((v) => (
                    <div key={v.venue} className="dtp-card">
                      <div className="dtp-card-name">{v.venue}</div>
                      <div className="dtp-kv"><span>Status</span><span className="dtp-pill up">{v.status}</span></div>
                      <div className="dtp-kv"><span>Mode</span><span className="dtp-pill sim">{v.mode}</span></div>
                      <div className="dtp-kv"><span>Protocol</span><span>{v.protocol?.toUpperCase()}</span></div>
                      <div className="dtp-kv"><span>FIX seq</span><span>{v.fix_seq}</span></div>
                    </div>
                  ))}
                </div>
                <Button icon={<ReloadOutlined />} onClick={loadVenues} style={{ marginTop: 12 }}>Refresh</Button>
              </section>
            )}

            {activeSection === 'instruments' && (
              <section>
                <h2><span className="dtp-flow">Flow 2</span> Instrument master &amp; bond analytics</h2>
                <p className="dtp-sub">Coupon, accrued interest, dirty price and YTM computed for the chosen settlement date.</p>
                <div className="dtp-row">
                  <div><label>Settlement date</label><input type="date" value={instSettle} onChange={(e) => setInstSettle(e.target.value)} /></div>
                  <button type="button" className="dtp-action" onClick={loadInstruments} disabled={loading}>Load analytics</button>
                </div>
                <table className="dtp-table">
                  <thead><tr><th>ISIN</th><th>Type</th><th>Issuer</th><th className="num">Coupon %</th>
                    <th className="num">Clean</th><th className="num">Accrued</th><th className="num">Dirty</th>
                    <th className="num">YTM %</th><th>Maturity</th><th>Rating</th></tr></thead>
                  <tbody>
                    {instrumentRows.map((r) => {
                      const i = r.instrument;
                      return (
                        <tr key={i.isin}>
                          <td>{i.isin}</td><td>{i.instrument_type}</td><td>{i.issuer}</td>
                          <td className="num">{i.coupon_rate ?? 'zero'}</td>
                          <td className="num">{fmt(r.indicative_price_clean, 4)}</td>
                          <td className="num">{fmt(r.accrued_interest, 4)}</td>
                          <td className="num">{fmt(r.indicative_price_dirty, 4)}</td>
                          <td className="num">{fmt(r.indicative_ytm_pct, 3)}</td>
                          <td>{i.maturity_date}</td><td>{i.credit_rating}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}

            {activeSection === 'inventory' && (
              <section>
                <h2><span className="dtp-flow">Flow 3</span> Inventory — tradable universe</h2>
                <p className="dtp-sub">Debt instruments available per (trade date, settlement date), merged across exchanges and brokers.</p>
                <div className="dtp-row">
                  <div><label>Trade date</label><input type="date" value={invTrade} onChange={(e) => setInvTrade(e.target.value)} /></div>
                  <div><label>Settlement date</label><input type="date" value={invSettle} onChange={(e) => setInvSettle(e.target.value)} /></div>
                  <button type="button" className="dtp-action secondary" onClick={refreshInventory}>Refresh from venues</button>
                  <button type="button" className="dtp-action" onClick={loadInventory}>Query</button>
                </div>
                {invBanner && <Banner type={invBanner.type} children={invBanner.text} />}
                <table className="dtp-table">
                  <thead><tr><th>ISIN</th><th>Venue</th><th className="num">Qty available</th>
                    <th className="num">Min lot</th><th className="num">Indicative price</th>
                    <th className="num">Indicative yield %</th></tr></thead>
                  <tbody>
                    {invRows.length ? invRows.map((row, idx) => (
                      <tr key={`${row.isin}-${row.venue}-${idx}`}>
                        <td>{row.isin}</td><td>{row.venue}</td>
                        <td className="num">{fmt(row.qty_available, 0)}</td>
                        <td className="num">{fmt(row.min_lot, 0)}</td>
                        <td className="num">{fmt(row.indicative_price, 4)}</td>
                        <td className="num">{fmt(row.indicative_yield, 3)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6}>No inventory — click &quot;Refresh from venues&quot; first.</td></tr>
                    )}
                  </tbody>
                </table>
              </section>
            )}

            {activeSection === 'rfq' && (
              <section>
                <h2><span className="dtp-flow">Flows 4–5</span> Price discovery (RFQ) &amp; quote hit</h2>
                <p className="dtp-sub">Quote request fans out to all exchanges + RFQ-capable brokers in parallel; hit a quote to trade at that price.</p>
                <form className="dtp-form" onSubmit={sendRfq}>
                  <div><label>ISIN</label>{renderIsinSelect(rfqIsin, setRfqIsin)}</div>
                  <div><label>Side</label><select value={rfqSide} onChange={(e) => setRfqSide(e.target.value)}><option>BUY</option><option>SELL</option></select></div>
                  <div><label>Qty (face value)</label><input value={rfqQty} onChange={(e) => setRfqQty(e.target.value)} /></div>
                  <div><label>Settlement date</label><input type="date" value={rfqSettle} onChange={(e) => setRfqSettle(e.target.value)} /></div>
                  <button type="submit" className="dtp-action">Request quotes</button>
                </form>
                {rfqBanner && <Banner type={rfqBanner.type} children={rfqBanner.text} />}
                <table className="dtp-table">
                  <thead><tr><th>Source</th><th className="num">Clean price</th><th className="num">Accrued</th>
                    <th className="num">Dirty price</th><th className="num">YTM %</th><th className="num">Qty avail</th><th /></tr></thead>
                  <tbody>
                    {rfqQuotes.map((q, i) => (
                      <tr key={q.quote_id} className={i === 0 ? 'best' : ''}>
                        <td>{q.source}{i === 0 ? ' ★' : ''}</td>
                        <td className="num">{fmt(q.price_clean, 4)}</td>
                        <td className="num">{fmt(q.accrued_interest, 4)}</td>
                        <td className="num">{fmt(q.price_dirty, 4)}</td>
                        <td className="num">{fmt(q.yield_pct, 3)}</td>
                        <td className="num">{fmt(q.qty_available, 0)}</td>
                        <td><button type="button" className="dtp-action small" onClick={() => hitQuote(q.quote_id, q.price_clean)}>Hit quote</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rfqHitResult && <div className="dtp-result"><Banner type={rfqHitResult.type} children={rfqHitResult.text} /></div>}
              </section>
            )}

            {activeSection === 'orders' && (
              <section>
                <h2><span className="dtp-flow">Flows 6–7</span> Order ticket &amp; blotter</h2>
                <p className="dtp-sub">AUTO routing sends G-Secs to RBI NDS-OM. Risk pre-trade checks reject bad orders — try a price far from market to see flow 7.</p>
                <form className="dtp-form" onSubmit={placeOrder}>
                  <div><label>ISIN</label>{renderIsinSelect(ordIsin, setOrdIsin)}</div>
                  <div><label>Side</label><select value={ordSide} onChange={(e) => setOrdSide(e.target.value)}><option>BUY</option><option>SELL</option></select></div>
                  <div><label>Type</label><select value={ordType} onChange={(e) => setOrdType(e.target.value)}><option>LIMIT</option><option>MARKET</option></select></div>
                  <div><label>Qty (face value)</label><input value={ordQty} onChange={(e) => setOrdQty(e.target.value)} /></div>
                  <div><label>Limit price (clean)</label><input value={ordPrice} onChange={(e) => setOrdPrice(e.target.value)} /></div>
                  <div><label>Settlement date</label><input type="date" value={ordSettle} onChange={(e) => setOrdSettle(e.target.value)} /></div>
                  <div><label>Venue</label><select value={ordVenue} onChange={(e) => setOrdVenue(e.target.value)}>{VENUE_OPTIONS.map((v) => <option key={v}>{v}</option>)}</select></div>
                  <button type="submit" className="dtp-action">Place order</button>
                </form>
                {ordBanner && <Banner type={ordBanner.type} children={ordBanner.text} />}
                <table className="dtp-table">
                  <thead><tr><th>ClOrdID</th><th>ISIN</th><th>Side</th><th className="num">Qty</th>
                    <th className="num">Filled</th><th className="num">Avg price</th><th>Venue</th><th>Status</th><th>Reason</th></tr></thead>
                  <tbody>
                    {blotter.map((o) => (
                      <tr key={o.cl_ord_id}>
                        <td>{o.cl_ord_id}</td><td>{o.isin}</td><td>{o.side}</td>
                        <td className="num">{fmt(o.qty_face_value, 0)}</td>
                        <td className="num">{fmt(o.filled_qty, 0)}</td>
                        <td className="num">{o.avg_fill_price ? fmt(o.avg_fill_price, 4) : '—'}</td>
                        <td>{o.routed_to}</td>
                        <td><span className={`dtp-pill ${o.status === 'REJECTED' ? 'rej' : 'ok'}`}>{o.status}</span></td>
                        <td>{o.reject_reason ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {activeSection === 'partner' && (
              <section>
                <h2><span className="dtp-flow">Flow 8</span> Partner gateway — inbound broker orders</h2>
                <p className="dtp-sub">External brokers submit orders with an API key; the platform applies partner limits and routes to the exchange.</p>
                <form className="dtp-form" onSubmit={submitPartnerOrder}>
                  <div><label>Partner API key</label><input value={pKey} onChange={(e) => setPKey(e.target.value)} /></div>
                  <div><label>ISIN</label>{renderIsinSelect(pIsin, setPIsin)}</div>
                  <div><label>Side</label><select value={pSide} onChange={(e) => setPSide(e.target.value)}><option>BUY</option><option>SELL</option></select></div>
                  <div><label>Qty (face value)</label><input value={pQty} onChange={(e) => setPQty(e.target.value)} /></div>
                  <div><label>Settlement date</label><input type="date" value={pSettle} onChange={(e) => setPSettle(e.target.value)} /></div>
                  <button type="submit" className="dtp-action">Submit partner order</button>
                </form>
                {pBanner && <Banner type={pBanner.type} children={pBanner.text} />}
                <table className="dtp-table">
                  <thead><tr><th>Partner</th><th>ClOrdID</th><th>ISIN</th><th className="num">Qty</th><th>Venue</th><th>Status</th></tr></thead>
                  <tbody>
                    {partnerOrders.map((p) => (
                      <tr key={p.order.cl_ord_id}>
                        <td>{p.partner_id}</td><td>{p.order.cl_ord_id}</td><td>{p.order.isin}</td>
                        <td className="num">{fmt(p.order.qty_face_value, 0)}</td>
                        <td>{p.order.routed_to}</td>
                        <td><span className={`dtp-pill ${p.order.status === 'REJECTED' ? 'rej' : 'ok'}`}>{p.order.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {activeSection === 'fixlog' && (
              <section>
                <h2><span className="dtp-flow">Flow 9</span> FIX 4.4 wire log</h2>
                <p className="dtp-sub">Raw archived FIX messages per venue session.</p>
                <div className="dtp-row">
                  <div><label>Venue session</label>
                    <select value={fixVenue} onChange={(e) => setFixVenue(e.target.value)}>
                      {FIX_VENUES.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label>Messages</label><input value={fixLimit} onChange={(e) => setFixLimit(e.target.value)} /></div>
                  <button type="button" className="dtp-action" onClick={loadFixLog}>Load</button>
                </div>
                <pre className="dtp-fixlog">{fixMessages.length ? fixMessages.join('\n') : '— no messages loaded —'}</pre>
              </section>
            )}

            {activeSection === 'posttrade' && (
              <section>
                <h2><span className="dtp-flow">Flow 10</span> Post-trade — obligations, settlement, positions</h2>
                <p className="dtp-sub">Fills become settlement obligations on the clearing rail; DvP settlement updates positions and cash.</p>
                <div className="dtp-row">
                  <div><label>Settlement date</label><input type="date" value={ptSettle} onChange={(e) => setPtSettle(e.target.value)} /></div>
                  <button type="button" className="dtp-action secondary" onClick={loadObligations}>Load obligations</button>
                  <button type="button" className="dtp-action" onClick={runSettlement}>Run DvP settlement</button>
                </div>
                {ptBanner && <Banner type={ptBanner.type} children={ptBanner.text} />}
                <h3>Obligations</h3>
                <table className="dtp-table">
                  <thead><tr><th>Rail</th><th>Side</th><th>ISIN</th><th className="num">Qty</th>
                    <th className="num">Funds ₹</th><th>Settle date</th><th>Status</th></tr></thead>
                  <tbody>
                    {obligations.length ? obligations.map((o) => (
                      <tr key={`${o.clearing_rail}-${o.isin}-${o.side}`}>
                        <td>{o.clearing_rail}</td><td>{o.side}</td><td>{o.isin}</td>
                        <td className="num">{fmt(o.qty_face_value, 0)}</td>
                        <td className="num">{fmt(o.funds_amount)}</td><td>{o.settlement_date}</td>
                        <td><span className={`dtp-pill ${o.status === 'SETTLED' ? 'ok' : 'sim'}`}>{o.status}</span></td>
                      </tr>
                    )) : <tr><td colSpan={7}>No obligations for this date — trade first.</td></tr>}
                  </tbody>
                </table>
                <h3>Positions (current user)</h3>
                <table className="dtp-table">
                  <thead><tr><th>ISIN</th><th>Account</th><th className="num">Qty (face value)</th><th className="num">Avg cost (dirty)</th></tr></thead>
                  <tbody>
                    {positions.length ? positions.map((p) => (
                      <tr key={`${p.isin}-${p.account_type}`}>
                        <td>{p.isin}</td><td>{p.account_type}</td>
                        <td className="num">{fmt(p.qty_face_value, 0)}</td>
                        <td className="num">{p.avg_cost_dirty ? fmt(p.avg_cost_dirty, 4) : '—'}</td>
                      </tr>
                    )) : <tr><td colSpan={4}>No settled positions yet.</td></tr>}
                  </tbody>
                </table>
                <h3>Contract notes (current user)</h3>
                <table className="dtp-table">
                  <thead><tr><th>Note</th><th>ISIN</th><th>Side</th><th className="num">Qty</th>
                    <th className="num">Clean</th><th className="num">Dirty</th><th className="num">Consideration ₹</th><th>Venue</th></tr></thead>
                  <tbody>
                    {contractNotes.length ? contractNotes.map((n) => (
                      <tr key={n.note_id}>
                        <td>{n.note_id}</td><td>{n.isin}</td><td>{n.side}</td>
                        <td className="num">{fmt(n.qty_face_value, 0)}</td>
                        <td className="num">{fmt(n.price_clean, 4)}</td>
                        <td className="num">{fmt(n.price_dirty, 4)}</td>
                        <td className="num">{fmt(n.consideration_inr)}</td><td>{n.venue}</td>
                      </tr>
                    )) : <tr><td colSpan={8}>No contract notes yet.</td></tr>}
                  </tbody>
                </table>
              </section>
            )}

            {activeSection === 'audit' && (
              <section>
                <h2><span className="dtp-flow">Flow 11</span> EOD reconciliation &amp; routing audit</h2>
                <p className="dtp-sub">Contract notes vs obligations recon, plus every smart-order-router decision with its reason.</p>
                <div className="dtp-row">
                  <button type="button" className="dtp-action" onClick={loadAudit}>Refresh</button>
                </div>
                {reconBanner && <Banner type={reconBanner.type} children={reconBanner.text} />}
                <table className="dtp-table">
                  <thead><tr><th>ClOrdID</th><th>ISIN</th><th>Routed to</th><th>Reason</th></tr></thead>
                  <tbody>
                    {auditRows.length ? auditRows.map((l) => (
                      <tr key={`${l.cl_ord_id}-${l.venue}`}>
                        <td>{l.cl_ord_id}</td><td>{l.isin}</td><td>{l.venue}</td><td>{l.reason}</td>
                      </tr>
                    )) : <tr><td colSpan={4}>No routing decisions yet.</td></tr>}
                  </tbody>
                </table>
              </section>
            )}
          </main>
        </div>
      </div>
    </LandingLayout>
  );
}

export default DebtTradingPlatformPage;
